import {Paper, Popover} from "@mantine/core";
import {useState} from "react";
import type {ParsedLocation} from "../../types/general.types.ts";
import type {UserLocation} from "../../types/swc.types.ts";
import {LocationDetails} from "./LocationDetails.tsx";

const MAP_SIZE = 300;
const GALAXY_MIN = -500;
const GALAXY_MAX = 500;
const GALAXY_SPAN = GALAXY_MAX - GALAXY_MIN;

interface VendorLocationMapProps {
    vendorLocation: ParsedLocation | null;
    userLocation: UserLocation | null;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const toMapPoint = (x: number, y: number) => {
    const px = ((x - GALAXY_MIN) / GALAXY_SPAN) * MAP_SIZE;
    const py = ((GALAXY_MAX - y) / GALAXY_SPAN) * MAP_SIZE;
    return {
        left: clamp(px, 0, MAP_SIZE),
        top: clamp(py, 0, MAP_SIZE),
    };
};

const getVendorGalaxyPoint = (location: ParsedLocation | null): {x: number; y: number} | null => {
    const galaxy = location?.coordinates?.galaxy;
    if (!galaxy) return null;
    if (typeof galaxy.x !== 'number' || typeof galaxy.y !== 'number') return null;
    return {x: galaxy.x, y: galaxy.y};
};

const getUserGalaxyPoint = (userLocation: UserLocation | null): {x: number; y: number} | null => {
    const galaxy = userLocation?.coordinates?.galaxy?.attributes;
    if (!galaxy) return null;
    if (typeof galaxy.x !== 'number' || typeof galaxy.y !== 'number') return null;
    return {x: galaxy.x, y: galaxy.y};
};

export default function VendorLocationMap({vendorLocation, userLocation}: VendorLocationMapProps) {
    const [vendorPopoverOpen, setVendorPopoverOpen] = useState(false);
    const vendorPoint = getVendorGalaxyPoint(vendorLocation);
    const userPoint = getUserGalaxyPoint(userLocation);

    const vendorDot = vendorPoint ? {...toMapPoint(vendorPoint.x, vendorPoint.y), ...vendorPoint} : null;
    const userDot = userPoint ? {...toMapPoint(userPoint.x, userPoint.y), ...userPoint} : null;

    return (
        <Paper withBorder radius="md" p="xs">
            <div
                style={{
                    width: MAP_SIZE,
                    height: MAP_SIZE,
                    position: 'relative',
                    borderRadius: 8,
                    overflow: 'hidden',
                    backgroundImage: 'url(https://assets.swc-forge.com/map-grey.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: '1px solid rgba(255,255,255,0.08)',
                }}
            >
                {vendorDot && vendorLocation ? (
                    <Popover
                        opened={vendorPopoverOpen}
                        position="left-start"
                        withArrow
                        shadow="md"
                        offset={8}
                        withinPortal
                    >
                        <Popover.Target>
                            <div
                                title={`Vendor location (${vendorDot.x}, ${vendorDot.y})`}
                                onMouseEnter={() => setVendorPopoverOpen(true)}
                                onMouseLeave={() => setVendorPopoverOpen(false)}
                                style={{
                                    position: 'absolute',
                                    left: vendorDot.left,
                                    top: vendorDot.top,
                                    width: 18,
                                    height: 18,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 3,
                                    cursor: 'pointer',
                                }}
                            >
                                <div
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        background: '#ef4444',
                                        border: '2px solid #111827',
                                        boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.2)',
                                        pointerEvents: 'none',
                                    }}
                                />
                            </div>
                        </Popover.Target>
                        <Popover.Dropdown
                            p="xs"
                            onMouseEnter={() => setVendorPopoverOpen(true)}
                            onMouseLeave={() => setVendorPopoverOpen(false)}
                        >
                            <LocationDetails location={vendorLocation} />
                        </Popover.Dropdown>
                    </Popover>
                ) : null}

                {userDot ? (
                    <div
                        title={`Your location (${userDot.x}, ${userDot.y})`}
                        style={{
                            position: 'absolute',
                            left: userDot.left,
                            top: userDot.top,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: '#a855f7',
                            border: '1px solid #ffffff',
                            boxShadow: '0 0 0 2px rgba(168, 85, 247, 0.25)',
                            zIndex: 4,
                            pointerEvents: 'none',
                        }}
                    />
                ) : null}
            </div>
        </Paper>
    );
}
